/**
 * The Frameworks page's body, in the findings queue's idiom: a summary strip
 * of the whole programme, health tabs and an order control, then one dense
 * row per framework — what it answers to, how much of it is covered, what is
 * open against it, its health and its lead. Rows are links; ↑/↓ (or j/k)
 * walk them, Enter opens one.
 *
 * @module components/regulation/frameworks/FrameworksList
 */
import { Rebuild } from "@lucasmarkes/hairline/react";
import { Link } from "@tanstack/react-router";
import { ArrowUpDownIcon, ChevronRightIcon, FileTextIcon, LandmarkIcon, SearchXIcon } from "lucide-react";
import { useRef, type KeyboardEvent, type ReactNode } from "react";

import { HairlineArt } from "~/components/ui/hairline-art";
import { cn } from "~/lib/utils";
import type { FrameworkDocumentRef, FrameworkRegimeRef } from "../../../state/governance";
import { Bone } from "../LoadingSkeletons";
import { SegmentedTabs } from "../PageHeader";
import { PersonGlyph } from "../monitor/linearGlyphs";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../../ui/select";
import { CoverageMeter, FrameworkTile, HealthLabel } from "./FrameworkGlyphs";
import {
  FRAMEWORK_ORDERS,
  HEALTH_TABS,
  type FrameworkOrder,
  type FrameworkRow,
  type FrameworksSummary,
  type HealthTab,
} from "./frameworkView";

/** Name · coverage · findings · documents · health · lead · chevron. Narrow: name, coverage, chevron. */
const COLUMNS =
  "grid grid-cols-[minmax(0,1fr)_6.5rem_1rem] items-center gap-x-4 md:grid-cols-[minmax(0,1fr)_8.5rem_4.5rem_4.5rem_6.5rem_1.5rem_1rem] lg:gap-x-6";

const plural = (count: number, one: string, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;

/* ------------------------------------------------------------------ */
/* Summary                                                             */
/* ------------------------------------------------------------------ */

function Stat({ label, children, hint }: { readonly label: string; readonly children: ReactNode; readonly hint?: ReactNode }) {
  return (
    <div className="min-w-0 bg-card px-4 py-3 sm:px-5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="mt-1 flex min-w-0 items-baseline gap-2">{children}</div>
      {hint ? <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

/** The programme at a glance: overall coverage, open findings, documents, regulators. */
export function FrameworksSummaryStrip({ summary }: { readonly summary: FrameworksSummary }) {
  const { coverage } = summary;
  return (
    <div
      data-frameworks-summary=""
      className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border/60 bg-border/60 sm:grid-cols-4"
    >
      <Stat
        label="Coverage"
        hint={coverage.total > 0 ? `${coverage.satisfied} of ${coverage.total} controls addressed` : "No controls catalogued"}
      >
        <span className="text-xl font-semibold tabular-nums tracking-tight text-foreground">
          {coverage.percent === null ? "—" : `${coverage.percent}%`}
        </span>
        {coverage.percent !== null ? (
          <CoverageMeter coverage={coverage} hideValue className="flex-1" barClassName="w-full max-w-24" />
        ) : null}
      </Stat>
      <Stat label="Open findings" hint={summary.openFindings > 0 ? "raised by their regimes' publications" : "nothing open"}>
        <span
          className={cn(
            "text-xl font-semibold tabular-nums tracking-tight",
            summary.openFindings > 0 ? "text-orange-600 dark:text-orange-400" : "text-foreground",
          )}
        >
          {summary.openFindings}
        </span>
      </Stat>
      <Stat label="Documents filed" hint={plural(summary.frameworks, "framework")}>
        <span className="text-xl font-semibold tabular-nums tracking-tight text-foreground">{summary.documents}</span>
      </Stat>
      <Stat label="Regulators" hint="behind the regimes they answer to">
        <span className="text-xl font-semibold tabular-nums tracking-tight text-foreground">{summary.regulators}</span>
      </Stat>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Toolbar                                                             */
/* ------------------------------------------------------------------ */

export function FrameworksToolbar({
  tab,
  onTabChange,
  counts,
  order,
  onOrderChange,
}: {
  readonly tab: HealthTab;
  readonly onTabChange: (tab: HealthTab) => void;
  readonly counts: Readonly<Record<HealthTab, number>>;
  readonly order: FrameworkOrder;
  readonly onOrderChange: (order: FrameworkOrder) => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="-mx-1 min-w-0 overflow-x-auto px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <SegmentedTabs
          label="Framework health"
          value={tab}
          onValueChange={onTabChange}
          tabs={HEALTH_TABS.filter(([key]) => key === "all" || counts[key] > 0 || key === tab).map(([value, label]) => ({
            value,
            label,
            count: counts[value],
          }))}
        />
      </div>
      <Select value={order} onValueChange={(value) => onOrderChange((value ?? "health") as FrameworkOrder)}>
        <SelectTrigger size="sm" className="w-auto min-w-0 gap-1.5 text-[13px]" aria-label="Order frameworks by">
          <ArrowUpDownIcon aria-hidden className="size-3.5 text-muted-foreground" />
          <span className="text-muted-foreground">Order:</span>
          <span>{FRAMEWORK_ORDERS.find(([key]) => key === order)?.[1]}</span>
        </SelectTrigger>
        <SelectContent>
          {FRAMEWORK_ORDERS.map(([key, label]) => (
            <SelectItem key={key} value={key}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Rows                                                                */
/* ------------------------------------------------------------------ */

/** "GFSC · Guernsey · 2 regimes", or what it lacks. */
export function frameworkSubline(row: FrameworkRow): string {
  const regimes = row.framework.regimes.length;
  if (regimes === 0) return "Answers to no regime yet";
  const regimeText = regimes === 1 ? row.framework.regimes[0]!.name : plural(regimes, "regime");
  return [...row.regulators, ...row.jurisdictions, regimeText].join(" · ");
}

function FrameworkListRow({ row, orgSegment }: { readonly row: FrameworkRow; readonly orgSegment: string }) {
  const { framework, rollup, coverage } = row;
  const documents = framework.documents.length;
  return (
    <li>
      <Link
        to="/$org/compliance/frameworks/$frameworkId"
        params={{ org: orgSegment, frameworkId: framework.id }}
        data-framework-row={framework.id}
        className={cn(
          COLUMNS,
          "group/row h-16 px-4 outline-none transition-colors duration-150 sm:px-5",
          "hover:bg-foreground/[0.03] focus-visible:bg-foreground/[0.05] focus-visible:shadow-[inset_2px_0_0_var(--brand-accent,var(--color-ring))]",
        )}
      >
        <span className="flex min-w-0 items-center gap-3">
          <FrameworkTile />
          <span className="min-w-0">
            <span className="block truncate text-[13px] font-medium text-foreground">{framework.name}</span>
            <span className="mt-0.5 block truncate text-xs text-muted-foreground" title={framework.regimes.map((regime) => regime.name).join(", ")}>
              {frameworkSubline(row)}
            </span>
          </span>
        </span>
        <CoverageMeter coverage={coverage} barClassName="max-md:w-10" />
        <span
          className="hidden items-center gap-1.5 text-[13px] tabular-nums md:flex"
          title={plural(framework.openFindings, "open finding")}
        >
          {framework.openFindings > 0 ? (
            <>
              <span aria-hidden className="size-2 rounded-full bg-orange-500 dark:bg-orange-400" />
              <span className="text-foreground/85">{framework.openFindings}</span>
            </>
          ) : (
            <span className="text-muted-foreground/60">—</span>
          )}
        </span>
        <span className="hidden items-center gap-1.5 text-[13px] tabular-nums text-foreground/85 md:flex" title={plural(documents, "document")}>
          {documents > 0 ? (
            <>
              <FileTextIcon aria-hidden className="size-3.5 text-muted-foreground" />
              {documents}
            </>
          ) : (
            <span className="text-muted-foreground/60">—</span>
          )}
        </span>
        <HealthLabel health={rollup.health} title={rollup.reasons.join(" · ")} className="hidden md:inline-flex" />
        <span className="hidden justify-center md:flex">
          <PersonGlyph size="md" email={framework.ownerEmail} title={framework.ownerEmail ? `Lead: ${framework.ownerEmail}` : "No lead"} />
        </span>
        <ChevronRightIcon
          aria-hidden
          className="size-4 text-muted-foreground/0 transition-colors duration-150 group-hover/row:text-muted-foreground group-focus-visible/row:text-muted-foreground"
        />
      </Link>
    </li>
  );
}

/** ↑/↓ and j/k move focus between rows; Home/End jump to the ends. */
function moveFocus(event: KeyboardEvent<HTMLUListElement>) {
  const keys: Record<string, number | "first" | "last"> = { ArrowDown: 1, j: 1, ArrowUp: -1, k: -1, Home: "first", End: "last" };
  const step = keys[event.key];
  if (step === undefined || event.metaKey || event.ctrlKey || event.altKey) return;
  const rows = [...event.currentTarget.querySelectorAll<HTMLElement>("[data-framework-row]")];
  if (rows.length === 0) return;
  const current = rows.indexOf(document.activeElement as HTMLElement);
  const next =
    step === "first" ? 0 : step === "last" ? rows.length - 1 : Math.max(0, Math.min(rows.length - 1, current < 0 ? 0 : current + step));
  event.preventDefault();
  rows[next]?.focus();
}

export function FrameworksListTable({
  rows,
  orgSegment,
}: {
  readonly rows: ReadonlyArray<FrameworkRow>;
  readonly orgSegment: string;
}) {
  const listRef = useRef<HTMLUListElement>(null);
  return (
    <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
      <div className={cn(COLUMNS, "h-9 border-b border-border/60 bg-muted/40 px-4 text-xs text-muted-foreground sm:px-5")}>
        <span>Framework</span>
        <span>Coverage</span>
        <span className="hidden md:block">Findings</span>
        <span className="hidden md:block">Documents</span>
        <span className="hidden md:block">Health</span>
        <span className="hidden text-center md:block">Lead</span>
        <span />
      </div>
      <ul ref={listRef} aria-label="Frameworks" onKeyDown={moveFocus} className="divide-y divide-border/50">
        {rows.map((row) => (
          <FrameworkListRow key={row.framework.id} row={row} orgSegment={orgSegment} />
        ))}
      </ul>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Unclaimed                                                           */
/* ------------------------------------------------------------------ */

const CHIP =
  "inline-flex max-w-72 items-center gap-1.5 rounded-md border border-border/70 bg-background px-2 py-1 text-xs text-foreground/85 transition-colors duration-150 hover:bg-foreground/[0.04] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/** Regimes no framework answers for, and documents filed under none. */
export function UnclaimedPanel({
  regimes,
  documents,
  orgSegment,
}: {
  readonly regimes: ReadonlyArray<FrameworkRegimeRef>;
  readonly documents: ReadonlyArray<FrameworkDocumentRef>;
  readonly orgSegment: string;
}) {
  if (regimes.length === 0 && documents.length === 0) return null;
  return (
    <section aria-labelledby="frameworks-unclaimed" className="rounded-xl border border-dashed border-border/70 px-4 py-3.5 sm:px-5">
      <h2 id="frameworks-unclaimed" className="text-[13px] font-medium text-foreground">
        Not yet organised
      </h2>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Nothing below counts towards a framework's coverage until it is claimed.
      </p>
      <div className="mt-3 space-y-2.5">
        {regimes.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="me-1 w-full text-xs text-muted-foreground sm:w-36">{plural(regimes.length, "regime")} in no framework</span>
            {regimes.map((regime) => (
              <Link key={regime.id} to="/$org/compliance/regimes/$regimeId" params={{ org: orgSegment, regimeId: regime.id }} className={CHIP} title={regime.name}>
                <LandmarkIcon aria-hidden className="size-3 shrink-0 text-muted-foreground" />
                <span className="truncate">{regime.name}</span>
              </Link>
            ))}
          </div>
        ) : null}
        {documents.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="me-1 w-full text-xs text-muted-foreground sm:w-36">{plural(documents.length, "document")} unfiled</span>
            {documents.map((document) => (
              <Link
                key={document.id}
                to="/$org/compliance/policies/$policyId"
                params={{ org: orgSegment, policyId: document.id }}
                className={CHIP}
                title={document.title ?? document.filename}
              >
                <FileTextIcon aria-hidden className="size-3 shrink-0 text-muted-foreground" />
                <span className="truncate">{document.title ?? document.filename}</span>
              </Link>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Loading and empty                                                   */
/* ------------------------------------------------------------------ */

/** The summary strip and the list, as bones in their real geometry. */
export function FrameworksListSkeleton({ rows = 4 }: { readonly rows?: number }) {
  return (
    <div role="status" aria-label="Loading frameworks" aria-busy="true" className="space-y-4">
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border/60 bg-border/60 sm:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="space-y-2 bg-card px-5 py-3">
            <Bone className="h-3 w-16" />
            <Bone className="h-6 w-12" />
          </div>
        ))}
      </div>
      <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
        <div className="h-9 border-b border-border/60 bg-muted/40" />
        <div className="divide-y divide-border/50">
          {Array.from({ length: rows }, (_, index) => (
            <div key={index} className={cn(COLUMNS, "h-16 px-4 sm:px-5")}>
              <span className="flex items-center gap-3">
                <Bone className="size-8 rounded-lg" />
                <span className="space-y-1.5">
                  <Bone className="h-3 w-48" />
                  <Bone className="h-2.5 w-64 max-w-full" />
                </span>
              </span>
              <Bone className="h-1.5 w-16" />
              <Bone className="hidden h-3 w-6 md:block" />
              <Bone className="hidden h-3 w-6 md:block" />
              <Bone className="hidden h-3 w-16 md:block" />
              <Bone className="hidden size-5 rounded-full md:block" />
              <span />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function FrameworksEmpty({
  icon = "figure",
  title,
  children,
  action,
}: {
  readonly icon?: "figure" | "search";
  readonly title: string;
  readonly children?: ReactNode;
  readonly action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center rounded-xl border border-dashed border-border/70 px-6 py-14 text-center">
      {icon === "search" ? (
        <span className="flex size-10 items-center justify-center rounded-xl border border-border/60 bg-muted/50">
          <SearchXIcon aria-hidden className="size-5 text-muted-foreground" />
        </span>
      ) : (
        <HairlineArt figure={Rebuild} className="mb-1 w-48 sm:w-56" />
      )}
      <p className="mt-3 text-sm font-medium text-foreground">{title}</p>
      {children ? <p className="mt-1 max-w-sm text-[13px] text-muted-foreground">{children}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}
